<template>
	<v-menu v-model="menu" eager disable-keys offset-y max-height="40%">
		<template v-slot:activator="{ attr }">
			<v-autocomplete
				v-model="localValue"
				:items="items"
				:item-text="itemText"
				:item-value="itemValue"
				:label="label"
				:search-input.sync="searchInput"
				multiple
				:menu-props="{ disabled: true }"
				:dense="dense"
				:outlined="outlined"
				:chips="chips"
				:smallChips="smallChips"
				:deletableChips="deletableChips"
				:hideDetails="hideDetails"
				:loading="loading"
				:disabled="disabled"
				:rules="rules"
				:class="inputClass"
				:clearable="clearable"
				@click.native="menu = true; $emit('click')"
				@focus="menu = true; $emit('focus')"
				@blur="$emit('blur')"
				@keydown="menu = true;"
				@keydown.down="menu = true"
				v-bind="attr"
			></v-autocomplete>
		</template>

		<v-list :dense="dense">
			<v-menu
				v-for="(item, i) in treeItems.filter(c =>
					!searchInput ||
					(
						typeof searchInput === 'string' &&
						c.children.findIndex(e =>
							(e[itemText] || '').toLowerCase().includes(searchInput.toLowerCase())
						) !== -1
					)
				)"
				:key="i"
				disable-keys
				content-class="elevation-3"
				min-width="0"
				offset-x
				:close-on-content-click="false"
			>
				<template v-slot:activator="{ attr, on }">						
					<v-list-item
						:class="{ 'active-menu-item': item.children.find(c => localValue.includes(c.id)) }"
						v-bind="attr"
						v-on="on"
					>
						<v-list-item-avatar v-if="item.image" size="22" tile>
							<v-img
								contain
								:src="item.image"
								lazy-src="@/assets/placeholder-image.png"
							/>
						</v-list-item-avatar>
						<v-list-item-content>
							<v-list-item-title>
								{{item[itemText]}}
							</v-list-item-title>
						</v-list-item-content>
						<v-list-item-action v-if="item.children && item.children.length">
							<v-icon>mdi-chevron-down</v-icon>
						</v-list-item-action>
					</v-list-item>
				</template>

				<v-list :dense="dense" width="fit-content">
					<v-list-item
						v-for="(child, i) in item.children.filter(c =>
							!searchInput ||
							(typeof searchInput === 'string' && (c[itemText] || '').toLowerCase().includes(searchInput.toLowerCase()))
						)"
						:key="i"
						:class="{ 'active-menu-item': localValue.includes(child.id) }"
						@click="applyMultiple(child.id); $emit('change');"
					>
						<v-checkbox
							v-if="multiple"
							:ripple="false"
							dense
							hide-details
							class="ma-0 pa-0"
							:input-value="localIds.includes(child.id)"
						></v-checkbox>
						<v-list-item-avatar v-if="child.image" size="22" tile>
							<v-img
								contain
								:src="child.image"
								lazy-src="@/assets/placeholder-image.png"
							/>
						</v-list-item-avatar>
						<v-list-item-title>
							{{child[itemText]}}
						</v-list-item-title>
					</v-list-item>
				</v-list>
			</v-menu>

			<v-list-item v-if="!filteredTreeItems.length || !items.length || !treeItems.length">
				<v-list-item-title>
					{{$vuetify.lang.locales[$vuetify.lang.current].noDataText}}
				</v-list-item-title>
			</v-list-item>
		</v-list>
	</v-menu>
</template>

<script>
export default {
	name: 'VTreeAutocomplete',

	props: {
		label: { type: String },
		items: { type: Array, default: () => [] },
		treeItems: { type: Array, default: () => [] },
		itemText: { type: String, default: 'name' },
		itemValue: { type: String, default: 'id' },
		chips: { type: Boolean, default: false },
		smallChips: { type: Boolean, default: false },
		deletableChips: { type: Boolean, default: false },
		dense: { type: Boolean, default: false },
		loading: { type: Boolean, default: false },
		disabled: { type: Boolean, default: false },
		outlined: { type: Boolean, default: false },
		hideDetails: { type: Boolean, default: false },
		rules: { type: Array, default: () => [] },
		value: { type: Array, default: () => [] },
		inputClass: { type: String },
		multiple: { type: Boolean, default: false },
		clearable: { type: Boolean, default: false }
	},

	data: () => ({
		menu: false,
		searchInput: null,
		localIds: []
	}),

	watch: {
		value (val) {
			this.localIds = val;
		}
	},

	computed: {
		localValue: {
			get() { return this.value },
			set(value) { return this.$emit('input', value) },
		},

		shownText() {
			return this.localValue.map(e => {
				const item = this.items.find(c => e === c.id);
				return {
					[this.itemValue]: item[this.itemValue],
					[this.itemText]: item[this.itemText],
				}
			});
		},

		filteredTreeItems() {
			return this.treeItems.filter(c =>
				!this.searchInput ||
				(
					typeof this.searchInput === 'string' &&
					c.children.findIndex(e =>
						(e[this.itemText] || '').toLowerCase().includes(this.searchInput.toLowerCase())
					) !== -1
				)
			)
		}
	},

	methods: {
		applyMultiple(id) {
			if (this.multiple) {
				if (this.localIds.includes(id)) {
					const index = this.localIds.findIndex(c => c === id);
					if (index !== -1) {
						this.localIds.splice(index, 1);
					}
				} else {
					this.localIds.push(id);
				}
			} else {
				this.localIds = [id];
				this.menu = false;
			}
			this.localValue = this.localIds;
		},
	},

	created	() {
		if (this.value) {
			this.localIds = this.value;
		}
	}
}
</script>

<style lang="scss">
.active-menu-item {
	background: #08456622;
}
</style>